import React, { FC, useContext, useEffect, useState } from 'react';
import { formatDate } from 'utils/format';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import {
    createApiClientTemplate,
    deleteApiClientTemplate,
    getApiClientsForOrganizationTemplate,
    rotateApiClientSecretTemplate,
    setApiClientDisabledTemplate,
} from 'apollo/templates/api-client';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import Loader from 'components/shared/loader/loader.component';
import { ApiClient, ApiClientWithSecret, CreateApiClientInput } from 'models/api-client';
import ApiClientSecretDialog from './api-client-secret-dialog.component';
import CreateApiClientDialog from './create-api-client-dialog.component';
import { OrganizationRef } from './types';

type PendingAction = { type: 'rotate'; client: ApiClient } | { type: 'delete'; client: ApiClient };

type ApiClientsTableProps = {
    organization: OrganizationRef | null;
};

const ApiClientsTable: FC<ApiClientsTableProps> = ({ organization }: ApiClientsTableProps) => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
    const [revealedSecret, setRevealedSecret] = useState<ApiClientWithSecret | null>(null);

    const [fetchClients, { data: clientsData, loading: clientsLoading }] = useLazyQuery<{
        apiClientsForOrganization: ApiClient[];
    }>(getApiClientsForOrganizationTemplate, {
        fetchPolicy: 'network-only',
        onError: showBoundary,
    });
    const clients = clientsData?.apiClientsForOrganization ?? [];

    useEffect(() => {
        if (organization?._id) {
            fetchClients({ variables: { organizationId: organization._id } });
        }
    }, [organization, fetchClients]);

    const refetchClients = () => {
        if (organization?._id) {
            fetchClients({ variables: { organizationId: organization._id } });
        }
    };

    const showToast = (message: string) => {
        setAppToasterContent({ isOpen: true, severity: 'success', message });
    };

    const [createApiClient] = useMutation<{ createApiClient: ApiClientWithSecret }>(createApiClientTemplate, {
        onCompleted: ({ createApiClient: result }) => {
            setRevealedSecret(result);
            refetchClients();
            showToast(translate('app-settings:API_CLIENT_CREATED'));
        },
        onError: showBoundary,
    });

    const [rotateSecret] = useMutation<{ rotateApiClientSecret: ApiClientWithSecret }>(rotateApiClientSecretTemplate, {
        onCompleted: ({ rotateApiClientSecret: result }) => {
            setRevealedSecret(result);
            refetchClients();
            showToast(translate('app-settings:API_CLIENT_ROTATED'));
        },
        onError: showBoundary,
    });

    const [setDisabled] = useMutation(setApiClientDisabledTemplate, {
        onCompleted: () => {
            refetchClients();
            showToast(translate('app-settings:API_CLIENT_UPDATED'));
        },
        onError: showBoundary,
    });

    const [deleteClient] = useMutation(deleteApiClientTemplate, {
        onCompleted: () => {
            refetchClients();
            showToast(translate('app-settings:API_CLIENT_DELETED'));
        },
        onError: showBoundary,
    });

    const handleCreate = (input: CreateApiClientInput) => {
        createApiClient({ variables: { input } });
    };

    const handleConfirmPending = () => {
        if (!pendingAction) return;
        if (pendingAction.type === 'rotate') {
            rotateSecret({ variables: { id: pendingAction.client._id } });
        } else if (pendingAction.type === 'delete') {
            deleteClient({ variables: { id: pendingAction.client._id } });
        }
        setPendingAction(null);
    };

    const handleToggleDisabled = (client: ApiClient) => {
        setDisabled({ variables: { id: client._id, disabled: !client.disabled } });
    };

    const formatDateOrDash = (iso?: string) => (iso ? formatDate(new Date(iso), 'datetime') : '—');

    return (
        <Box>
            <Typography variant="h2" gutterBottom>
                {translate('app-settings:API_CLIENTS')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('app-settings:API_CLIENTS_HELP')}
            </Typography>

            <Box display="flex" gap={2} alignItems="center" mb={3}>
                <Button variant="contained" onClick={() => setIsCreateOpen(true)} disabled={!organization}>
                    {translate('app-settings:CREATE_API_CLIENT')}
                </Button>
            </Box>

            {organization && clientsLoading && <Loader />}

            {organization && !clientsLoading && (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('app-settings:API_CLIENT_NAME')}</TableCell>
                                <TableCell>{translate('app-settings:API_CLIENT_ID')}</TableCell>
                                <TableCell>{translate('app-settings:API_CLIENT_MODE')}</TableCell>
                                <TableCell>{translate('app-settings:API_CLIENT_SCOPES')}</TableCell>
                                <TableCell>{translate('app-settings:API_CLIENT_LAST_USED')}</TableCell>
                                <TableCell>{translate('app-settings:API_CLIENT_SECRET_ROTATED')}</TableCell>
                                <TableCell align="center">{translate('app-settings:API_CLIENT_ENABLED')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {clients.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {translate('app-settings:API_CLIENTS_EMPTY')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {clients.map((client) => (
                                <TableRow key={client._id}>
                                    <TableCell>
                                        <Typography variant="body2">{client.name}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontFamily="monospace">
                                            {client.clientId}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={client.mode}
                                            color={client.mode === 'PROD' ? 'primary' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={0.5} flexWrap="wrap">
                                            {client.scopes.map((scope) => (
                                                <Chip key={scope} size="small" label={scope} />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{formatDateOrDash(client.lastUsedAt)}</TableCell>
                                    <TableCell>{formatDateOrDash(client.secretLastRotatedAt)}</TableCell>
                                    <TableCell align="center">
                                        <Switch
                                            checked={!client.disabled}
                                            onChange={() => handleToggleDisabled(client)}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={translate('app-settings:ROTATE_SECRET')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setPendingAction({ type: 'rotate', client })}
                                            >
                                                <AutorenewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={translate('common:DELETE')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setPendingAction({ type: 'delete', client })}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {organization && (
                <CreateApiClientDialog
                    open={isCreateOpen}
                    organizationId={organization._id}
                    organizationName={organization.name}
                    onCancel={() => setIsCreateOpen(false)}
                    onSubmit={(input) => {
                        setIsCreateOpen(false);
                        handleCreate(input);
                    }}
                />
            )}

            <ConfirmDialog
                open={pendingAction !== null}
                handleClose={() => setPendingAction(null)}
                handleConfirm={handleConfirmPending}
                dialogTitle={
                    pendingAction?.type === 'rotate'
                        ? translate('app-settings:ROTATE_SECRET_CONFIRM_TITLE')
                        : translate('app-settings:DELETE_API_CLIENT_CONFIRM_TITLE')
                }
                dialogContent={
                    pendingAction?.type === 'rotate'
                        ? translate('app-settings:ROTATE_SECRET_CONFIRM_TEXT', {
                              name: pendingAction.client.name,
                          })
                        : pendingAction
                          ? translate('app-settings:DELETE_API_CLIENT_CONFIRM_TEXT', {
                                name: pendingAction.client.name,
                            })
                          : undefined
                }
            />

            {revealedSecret && (
                <ApiClientSecretDialog
                    open
                    clientId={revealedSecret.apiClient.clientId}
                    clientSecret={revealedSecret.clientSecret}
                    onClose={() => setRevealedSecret(null)}
                />
            )}
        </Box>
    );
};

export default ApiClientsTable;
